import React, { useState, useEffect, useMemo } from "react";
import apiClient from "../utils/apiClient";
import {
  useTable,
  usePagination,
  useGlobalFilter,
  useSortBy,
} from "react-table";
import Select from "react-select";
import { SERVER_BASE } from "../config/server";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faEllipsisV, faPencil, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { hasPermission } from "../utils/usePermissions";
import { useNavigate } from "react-router-dom";
import moment from "moment";
const GlobalFilter = ({ globalFilter, setGlobalFilter }) => {
  return (
    <input
      value={globalFilter || ""}
      onChange={(e) => setGlobalFilter(e.target.value)}
      placeholder="Search purposes..."
      className="form-control col-md-9"
    // style={{ maxWidth: "300px" }}
    />
  );
};

const PurposeListing = ({ permissions }) => {
  const [ledgerGroups, setLedgerGroups] = useState([]);
  const [purposes, setPurposes] = useState([]);
  const [purposeName, setPurposeName] = useState("");
  const [hindiPurposeName, setHindiPurposeName] = useState("");
  const [selectedLedgerGroups, setSelectedLedgerGroups] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [editIndex, setEditIndex] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const code = localStorage.getItem("code");
  const role = localStorage.getItem("role");
  const token = localStorage.getItem("token");

  const [loadingData, setLoadingData] = useState(true);
  const navigate = useNavigate();
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        await Promise.all([fetchLedgerGroups(), fetchPurposes()]);
      } catch (err) {
        console.error("Initial data fetch failed", err);
      } finally {
        setLoadingData(false); // ✅ Hide spinner when both are done
      }
    };
    fetchInitialData();
  }, []);

  const fetchLedgerGroups = async () => {
    try {
      if (!token) {
        console.error("Unauthorized: No token found.");
        return;
      }

      // Make the request with the Authorization header
      const response = await apiClient.get(SERVER_BASE + "/ledger-groups", {
        params: { code },
        headers: {
          Authorization: `Bearer ${token}`, // Include the JWT token in headers
        },
      });

      // Update the ledger groups state
      setLedgerGroups(response.data);
    } catch (error) {
      console.error("Error fetching ledger groups:", error);
    }
  };

  const fetchPurposes = async () => {
    try {
      // Retrieve the token from localStorage
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("Unauthorized: No token found.");
        return;
      }
      // Make the request with the Authorization header
      const response = await apiClient.get(SERVER_BASE + "/getpurposes", {
        headers: {
          Authorization: `Bearer ${token}`, // Include the JWT token in headers
        },
      });
      // Update the purposes state 
      setPurposes(response.data);
    } catch (error) {
      console.error("Error fetching purposes:", error);
    }
  };

  const handleEdit = (index) => {
    const purpose = purposes[index];
    navigate("../purposeLedgerPage", {
      state: { rowData: purpose, index: index },
    });
  };

  const handleDelete = (index) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You will not be able to recover this purpose!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await apiClient.delete(
            `${SERVER_BASE}/deletePurposes/${purposes[index].purpose_id}`,
            {
              headers: {
                Authorization: `Bearer ${token}`, // Add the JWT token in the header
              },
              data: { code },
              // Include 'code' in the delete request
            }
          );
          setPurposes((prev) => prev.filter((_, i) => i !== index));
          Swal.fire("Deleted!", "Your purpose has been deleted.", "success");
        } catch (error) {
          console.error("Error deleting purpose:", error);
          Swal.fire(
            "Error",
            "There was an issue deleting the purpose. Please try again later.",
            "error"
          );
        }
      }
    });
  };

  const columns = useMemo(
    () => [
      {
        Header: "S.No",
        Cell: ({ row }) => row.index + 1,
      },
      { Header: "Purpose", accessor: "purpose_name" }, // Assuming the column name is `purpose_name` in the data
      {
        Header: "Ledger Groups",
        accessor: "ledgerGroups", // This still points to the ledgerGroups array
        Cell: ({ cell: { value } }) => (
          <span>
            {value.map((group, index) => group.ledger_name).join(", ")}
          </span>
        ),
      },
      { Header: "Applicable On", accessor: "applicableOn" },
      {
        Header: "#",
        accessor: "Action",
        Cell: ({ row }) => {
          const {
            createdAt,
            createdByName,
            updatedAt,
            updatedByName,
          } = row.original;
          return (
            <div className="dropdown">
              <button
                className="btn btn-sm btn-light"
                type="button"
                id={`dropdownMenuButton-${row.index}`}
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <FontAwesomeIcon icon={faEllipsisV} />
              </button>

              <ul
                className="dropdown-menu"
                aria-labelledby={`dropdownMenuButton-${row.index}`}
              >
                {(role === "itManager" || permissions?.edit) && (
                  <li>
                    <button
                      className="dropdown-item"
                      onClick={() => handleEdit(row.index)}
                    >
                      <FontAwesomeIcon icon={faEdit} className="me-2" />
                      Edit
                    </button>
                  </li>
                )}

                {(role === "itManager" || permissions?.delete) && (
                  <li>
                    <button
                      className="dropdown-item"
                      onClick={() => handleDelete(row.index)}
                    >
                      <FontAwesomeIcon icon={faTrash} className="me-2" />
                      Delete
                    </button>
                  </li>
                )}
                {(createdByName || updatedByName) && (
                  <li>
                    <hr className="dropdown-divider" />
                  </li>
                )}
                {createdByName && (
                  <li className="dropdown-item">
                    <FontAwesomeIcon icon={faPlus} /> {createdByName}
                    {createdAt &&
                      ` (${moment(createdAt).format("DD-MMM-YY hh:mm A")})`}
                  </li>
                )}

                {/* Updated Info */}
                {updatedByName && (
                  <li className="dropdown-item">
                    <FontAwesomeIcon icon={faPencil} /> {updatedByName}
                    {updatedAt &&
                      ` (${moment(updatedAt).format("DD-MMM-YY hh:mm A")})`}
                  </li>
                )}
              </ul>
            </div>
          );
        },
      }
    ],
    [purposes]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    setGlobalFilter,
    canPreviousPage,
    canNextPage,
    pageOptions,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize, globalFilter },
  } = useTable(
    {
      columns,
      data: purposes,
      initialState: { pageIndex: 0, pageSize: 30 }, // min-20 page size
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );
  if (loadingData) {
    return (
      <div
        className="d-flex flex-column justify-content-center align-items-center"
        style={{ height: "60vh" }}
      >
        <div
          className="spinner-border text-success"
          role="status"
          style={{ width: "3rem", height: "3rem" }}
        >
          <span className="visually-hidden">Loading...</span>
        </div>
        <div className="mt-3 fs-5">Loading Purposes...</div>
      </div>
    );
  }
  return (
    <div className="container mt-5">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3>Purpose Management - Ledger</h3>
        <div className="ledger-count">
          Total Purpose: <strong>{purposes.length}</strong>
        </div>
      </div>

      <div className="formcontainer table-responsive">
        {/* 🔍 Search Input */}
        <div className="row align-items-center mb-3">
          {/* Add Purpose Button */}
          {(role === "itManager" || permissions?.write) && (
            <div className="col-auto">
              <button
                className="btn btn-outline-success"
                onClick={() => navigate("../purposeLedgerPage")}
              >
                Add Purpose
              </button>
            </div>
          )}

          {/* Global Filter */}
          {(role === "itManager" || permissions?.read) && (
            <div className="col">
              <GlobalFilter
                globalFilter={globalFilter}
                setGlobalFilter={setGlobalFilter}
              />
            </div>
          )}

          {/* Page Size Dropdown */}
          {(role === "itManager" || permissions?.read) && (
            <div className="col-auto">
              <label className="d-inline-flex align-items-center">
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="form-select w-auto"
                >
                  {[20, 30, 50, 100].map((size) => (
                    <option key={size} value={size}>
                      Show {size}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}
        </div>

        {(role == "itManager" || permissions?.read) && (
          <>
            <table {...getTableProps()} className="table table-striped">
              <thead>
                {headerGroups.map((headerGroup) => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <th
                        {...column.getHeaderProps(
                          column.getSortByToggleProps()
                        )}
                      >
                        {column.render("Header")}
                        <span>
                          {column.isSorted
                            ? column.isSortedDesc
                              ? " 🔽"
                              : " 🔼"
                            : ""}
                        </span>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {page.map((row) => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map((cell) => (
                        <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div className="d-flex align-items-center mb-3">
              {/* Previous – left corner */}
              <button
                onClick={() => previousPage()}
                disabled={!canPreviousPage}
                className="btn btn-secondary me-auto"
                style={{ minWidth: 110 }} // keeps width consistent
              >
                Previous
              </button>

              {/* Center block for page index & size selector */}
              <div className="text-center flex-grow-1 d-flex flex-column align-items-center">
                {/* Row 1: Page index and size selector in one line */}
                <div className="d-flex align-items-center gap-3">
                  <div className="fw-semibold">
                    Page {pageIndex + 1} of {pageOptions.length}
                  </div>


                </div>
              </div>

              {/* Next – right corner */}
              <button
                onClick={() => nextPage()}
                disabled={!canNextPage}
                className="btn btn-secondary ms-auto"
                style={{ minWidth: 110 }}
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PurposeListing;
